import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import HasSourceObject from "@mapmycustomers/shared/types/base/HasSourceObject";
import { EntityTypesSupportingCustomSourceObject } from "@mapmycustomers/shared/types/entity";
import SourceObjectValue from "@mapmycustomers/shared/types/SourceObjectValue";

import i18nService from "@app/config/I18nService";
import { getSourceObjectLabel } from "@app/util/ui";

import SourceObjectFilter, {
  SOURCE_OBJECT_FILTER_OPERATORS,
} from "../../filters/SourceObjectFilter";

import Field, { FieldProperties } from "./Field";

interface SourceObjectFieldProperties extends Omit<FieldProperties, "type"> {
  entityType: EntityTypesSupportingCustomSourceObject;
}

class SourceObjectField extends Field {
  private readonly _entityType: EntityTypesSupportingCustomSourceObject;
  private _sourceObjects: SourceObjectValue[] = [];

  constructor({ entityType, ...data }: SourceObjectFieldProperties) {
    super({
      customFilterConfig: {
        defaultInstance: "sourceObject",
        filterInstances: {
          sourceObject: SourceObjectFilter,
        },
        operators: SOURCE_OBJECT_FILTER_OPERATORS,
      },
      customGridProperties: {
        cellRenderer: "sourceObjectCellRenderer",
      },
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.SORTABLE,
        FieldFeature.SOURCE_OBJECT_FIELD,
        FieldFeature.SUPPORTS_SET_FILTER,
      ],
      type: FieldType.STRING,
      ...data,
    });
    this._entityType = entityType;
  }

  // Injected at app init from users/me; empty until then, so labels fall back to raw keys
  setSourceObjects(values: SourceObjectValue[]) {
    this._sourceObjects = values;
  }

  hasNonEmptyValueFor(entity: unknown): boolean {
    return this.getValueFor(entity) !== undefined; // null is the standard object, not "no value"
  }

  protected formatValue(entity: unknown, value: unknown): string {
    return (
      getSourceObjectLabel(
        i18nService.getIntl(),
        this._entityType,
        this._sourceObjects,
        value as HasSourceObject["sourceObject"] | undefined
      ) ?? ""
    );
  }
}

export default SourceObjectField;
